/**
 * SendCohortEventInvitesModal Component
 * Modal for sending Google Calendar invitations for cohort events (MED-92 / E8-14)
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  TextInput,
  Textarea,
  Text,
  Paper,
  Divider,
  Checkbox,
  ScrollArea,
  Loader,
  Center,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Video, Clock, AlertTriangle } from 'react-feather';
import { formatDateTime } from '../../../../utils/formatters';
import {
  cohortEventsApi,
  type CohortEventType,
  type CohortEventWithDetails,
} from '../../../../services/api/cohortEvents';
import {
  cohortCoachesApi,
  type CohortCoachWithDetails,
} from '../../../../services/api/cohortCoaches';
import { participantsApi } from '../../../../services/api/participants';
import type { Participant } from '../../../../types';

interface SendCohortEventInvitesModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  event: CohortEventWithDetails | null;
  /** Callback after invites are sent */
  onSent?: () => void;
  /** Mode: send for first time, resend to add more recipients */
  mode?: 'send' | 'resend';
}

// Coach-only events should not pre-select participants (MED-836).
// Exhaustive switch — adding a new CohortEventType without updating this map is a compile error.
function getDefaultRecipientSelection(
  eventType: CohortEventType,
): { defaultCoaches: boolean; defaultParticipants: boolean } {
  switch (eventType) {
    case 'coach_immersion':
    case 'coach_huddle':
      return { defaultCoaches: true, defaultParticipants: false };
    case 'participant_orientation':
    case 'cohort_wrap_up':
    case 'in_person_booster':
      return { defaultCoaches: true, defaultParticipants: true };
    default: {
      const _exhaustive: never = eventType;
      return _exhaustive;
    }
  }
}

export function SendCohortEventInvitesModal({
  opened,
  onClose,
  cohortId,
  event,
  onSent,
  mode = 'send',
}: SendCohortEventInvitesModalProps) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpened, setConfirmOpened] = useState(false);
  const [videoLink, setVideoLink] = useState('');
  const [additionalEmails, setAdditionalEmails] = useState('');

  // Recipient data
  const [coaches, setCoaches] = useState<CohortCoachWithDetails[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);

  // Selected recipient IDs
  const [selectedCoachIds, setSelectedCoachIds] = useState<Set<string>>(new Set());
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<Set<string>>(new Set());

  // Load recipients when modal opens
  useEffect(() => {
    if (!opened || !event) return;

    const loadRecipients = async () => {
      setLoading(true);
      try {
        const fetches: [
          Promise<{ data: CohortCoachWithDetails[] }>,
          Promise<{ data: Participant[] }>,
          Promise<{ invitations: Array<{ email: string; recipient_type: string }> } | null>,
        ] = [
          cohortCoachesApi
            .list(cohortId, { limit: 100 })
            .catch(() => ({ data: [] as CohortCoachWithDetails[] })),
          participantsApi
            .listBasic(cohortId, { status: 'active', limit: 100 })
            .catch(() => ({ data: [] as Participant[] })),
          // In resend mode, load existing invitations to pre-populate external emails
          mode === 'resend'
            ? cohortEventsApi.getRsvps(cohortId, event.id).catch(() => null)
            : Promise.resolve(null),
        ];

        const [coachesData, participantsData, existingRsvps] = await Promise.all(fetches);

        setCoaches(coachesData.data);
        setParticipants(participantsData.data);

        // Defaults driven by event_type — coach-only events (e.g. coach_immersion) skip participants by default (MED-836).
        const { defaultCoaches, defaultParticipants } = getDefaultRecipientSelection(
          event.event_type,
        );
        setSelectedCoachIds(
          defaultCoaches ? new Set(coachesData.data.map((c) => c.user_id)) : new Set(),
        );
        setSelectedParticipantIds(
          defaultParticipants ? new Set(participantsData.data.map((p) => p.user_id)) : new Set(),
        );

        // Pre-populate external emails from existing invitations in resend mode
        if (existingRsvps?.invitations) {
          const externalEmails = existingRsvps.invitations
            .filter((inv) => inv.recipient_type === 'external')
            .map((inv) => inv.email);
          if (externalEmails.length > 0) {
            setAdditionalEmails(externalEmails.join('\n'));
          }
        }
      } catch {
        // Silently handle - empty lists will show
      } finally {
        setLoading(false);
      }
    };

    loadRecipients();
    setVideoLink(event.video_link || event.default_meeting_link || '');
    setAdditionalEmails('');
  }, [opened, cohortId, event, mode]);

  // Toggle helpers
  const toggleCoach = (userId: string) => {
    setSelectedCoachIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleAllCoaches = () => {
    if (selectedCoachIds.size === coaches.length) {
      setSelectedCoachIds(new Set());
    } else {
      setSelectedCoachIds(new Set(coaches.map((c) => c.user_id)));
    }
  };

  const toggleParticipant = (userId: string) => {
    setSelectedParticipantIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleAllParticipants = () => {
    if (selectedParticipantIds.size === participants.length) {
      setSelectedParticipantIds(new Set());
    } else {
      setSelectedParticipantIds(new Set(participants.map((p) => p.user_id)));
    }
  };

  // Parse additional emails
  const parsedAdditionalEmails = useMemo(() => {
    return additionalEmails
      .split('\n')
      .map((e) => e.trim())
      .filter((e) => e.length > 0 && e.includes('@'));
  }, [additionalEmails]);

  const totalRecipients =
    selectedCoachIds.size + selectedParticipantIds.size + parsedAdditionalEmails.length;

  const handleSubmit = async () => {
    if (!event) return;

    if (totalRecipients === 0) {
      notifications.show({
        title: 'No Recipients',
        message: 'Please select at least one recipient',
        color: 'red',
      });
      return;
    }

    setSubmitting(true);
    try {
      const requestData = {
        video_link: videoLink.trim() || undefined,
        coach_ids: Array.from(selectedCoachIds),
        participant_ids: Array.from(selectedParticipantIds),
        additional_emails: parsedAdditionalEmails,
      };

      const apiMethod =
        mode === 'resend' ? cohortEventsApi.resendInvites : cohortEventsApi.sendInvites;
      const result = await apiMethod(cohortId, event.id, requestData);

      notifications.show({
        title: mode === 'resend' ? 'Invites Resent' : 'Invites Sent',
        message: `Invitations sent to ${result.invitations_created} recipients`,
        color: 'green',
      });

      onSent?.();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to send invitations',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!event) return null;

  const isResend = mode === 'resend';
  const modalTitle = isResend ? `Resend Invites: ${event.event_name}` : `Send Invites: ${event.event_name}`;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={4}>
          <Text fw={600} size="lg">
            {modalTitle}
          </Text>
          <Text size="sm" c="dimmed">
            {event.cohort_name}
          </Text>
        </Stack>
      }
      size="lg"
    >
      {loading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : (
        <Stack gap="md">
          {/* Event Details */}
          <Paper withBorder p="md">
            <Stack gap="xs">
              <Group gap="xs">
                <Clock size={14} color="var(--mantine-color-gray-6)" />
                <Text size="sm" fw={500}>
                  {event.scheduled_start
                    ? formatDateTime(event.scheduled_start)
                    : 'No date/time set'}
                </Text>
              </Group>
            </Stack>
          </Paper>

          {/* Video Link */}
          <TextInput
            label="Video Conferencing Link"
            placeholder="Enter meeting URL"
            description={
              event.default_meeting_link
                ? 'Pre-filled from cohort default meeting link'
                : undefined
            }
            leftSection={<Video size={16} />}
            value={videoLink}
            onChange={(e) => setVideoLink(e.currentTarget.value)}
          />

          <Divider />

          {/* Recipients */}
          <Text fw={600} size="sm">
            Select Recipients
          </Text>

          {/* Coaches */}
          <Paper withBorder p="sm">
            <Stack gap="xs">
              <Checkbox
                label={`All Coaches (${coaches.length})`}
                checked={coaches.length > 0 && selectedCoachIds.size === coaches.length}
                indeterminate={selectedCoachIds.size > 0 && selectedCoachIds.size < coaches.length}
                onChange={toggleAllCoaches}
                fw={500}
              />
              {coaches.length > 0 && (
                <ScrollArea.Autosize mah={150} ml="md">
                  <Stack gap={4}>
                    {coaches.map((coach) => (
                      <Checkbox
                        key={coach.user_id}
                        label={`${coach.first_name || ''} ${coach.last_name || ''} (${coach.email})`}
                        checked={selectedCoachIds.has(coach.user_id)}
                        onChange={() => toggleCoach(coach.user_id)}
                        size="sm"
                      />
                    ))}
                  </Stack>
                </ScrollArea.Autosize>
              )}
              {coaches.length === 0 && (
                <Text size="xs" c="dimmed" ml="md">
                  No coaches found in this cohort
                </Text>
              )}
            </Stack>
          </Paper>

          {/* Participants */}
          <Paper withBorder p="sm">
            <Stack gap="xs">
              <Checkbox
                label={`All Participants (${participants.length})`}
                checked={
                  participants.length > 0 &&
                  selectedParticipantIds.size === participants.length
                }
                indeterminate={
                  selectedParticipantIds.size > 0 &&
                  selectedParticipantIds.size < participants.length
                }
                onChange={toggleAllParticipants}
                fw={500}
              />
              {participants.length > 0 && (
                <ScrollArea.Autosize mah={150} ml="md">
                  <Stack gap={4}>
                    {participants.map((p) => (
                      <Checkbox
                        key={p.user_id}
                        label={`${p.first_name} ${p.last_name} (${p.email})`}
                        checked={selectedParticipantIds.has(p.user_id)}
                        onChange={() => toggleParticipant(p.user_id)}
                        size="sm"
                      />
                    ))}
                  </Stack>
                </ScrollArea.Autosize>
              )}
              {participants.length === 0 && (
                <Text size="xs" c="dimmed" ml="md">
                  No active participants found
                </Text>
              )}
            </Stack>
          </Paper>

          <Divider />

          {/* Additional Emails */}
          <Textarea
            label="Additional Email Addresses"
            description="Enter email addresses for external invitees (one per line)"
            placeholder="jane@example.com&#10;john@example.com"
            value={additionalEmails}
            onChange={(e) => setAdditionalEmails(e.currentTarget.value)}
            minRows={2}
            autosize
          />

          <Divider />

          {/* Summary */}
          <Text size="sm" c="dimmed" ta="center">
            {totalRecipients} recipient{totalRecipients !== 1 ? 's' : ''} selected
          </Text>

          {/* Actions */}
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={() => setConfirmOpened(true)} loading={submitting} disabled={totalRecipients === 0}>
              {isResend ? 'Resend Invites' : 'Send Invites'}
            </Button>
          </Group>
        </Stack>
      )}

      {/* MED-646: Send confirmation dialog */}
      <Modal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        title="Confirm Invite Send"
        size="sm"
        centered
        zIndex={301}
      >
        <Stack gap="md">
          <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
            <Text size="sm">
              You are about to {isResend ? 'resend' : 'send'} calendar invitations to{' '}
              <Text component="span" fw={700}>
                {totalRecipients} recipient{totalRecipients !== 1 ? 's' : ''}
              </Text>
              . This action cannot be undone.
            </Text>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setConfirmOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              onClick={() => {
                setConfirmOpened(false);
                handleSubmit();
              }}
              loading={submitting}
              disabled={submitting}
            >
              Confirm &amp; {isResend ? 'Resend' : 'Send'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
}

export default SendCohortEventInvitesModal;
